import Vue from "vue";
import dayjs from "dayjs";
import Toast from "vue-toastification"; // Import the CSS or use your own!
import "vue-toastification/dist/index.css";
import { deleteToken, getMessaging } from "firebase/messaging";
import app from "@/plugins/firebase.app";

const toastOptions = {
  timeout: 2500,
};
Vue.use(Toast, toastOptions);

export default Vue.mixin({
  methods: {
    $capitalFirstLetter(s) {
      if (!s) return "";
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    $formatDate(date) {
      if (date === null || date === undefined || date === "") return null;
      const d = dayjs(date);
      if (!d.isValid()) return "";
      return d.format("DD.MM.YYYY");
    },

    $formatDateAndHourMinute(date) {
      if (date === null || date === undefined || date === "") return null;
      if (dayjs(date).isValid()) {
        return dayjs(date).format("DD.MM.YYYY HH:mm");
      } else {
        return "Päivämäärä ei ole oikein";
      }
    },
    $formatWeek(date) {
      if (date === null || date === undefined || date === "") return null;
      const d = dayjs(date);
      if (!d.isValid()) return "";
      return d.format("WW/GGGG");
    },
    $formatWeekDayDateAndHourMinute(date) {
      if (!date) return "";
      const d = dayjs(date);
      if (!d.isValid()) return "";
      return d.format("dd DD.MM.YYYY HH:mm");
    },
    $formatWeekDayDate(date) {
      if (!date) return "";
      const d = dayjs(date);
      if (!d.isValid()) return "";
      return d.format("dd DD.MM.YYYY");
    },
    $parseDateAsHelsinki(date) {
      if (!date || typeof date !== "string" || !date.trim()) {
        return dayjs(null);
      }
      try {
        const parsed = dayjs.tz(date, "YYYY-MM-DD HH:mm", "Europe/Helsinki");
        if (parsed.isValid()) return parsed;
      } catch (e) {
        // invalid date format for dayjs.tz
      }
      try {
        const fallback = dayjs(date);
        if (fallback.isValid()) return fallback;
      } catch (e) {
        // invalid date
      }
      return dayjs(null);
    },
    $formatWeekDay(date) {
      if (!date) return "";
      const d = dayjs(date);
      if (!d.isValid()) return "";
      return d.format("dd");
    },
    $formatMonthYear(date) {
      if (!date) return "";
      const d = dayjs(date);
      if (!d.isValid()) return "";
      return d.format("MMMM YYYY");
    },
    async $logout() {
      await this.$store.dispatch("logout");
      this.$router.push({ name: "Login" });
      try {
        const messaging = getMessaging(app);
        await deleteToken(messaging);
      } catch (e) {
        console.error(e);
      }
    },
    $id(item) {
      return item?.id;
    },
    $uuidv4() {
      return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (
          c ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16),
      );
    },
    $deleteConfirm(title) {
      return this.$confirm(title, {
        buttonTrueText: "Kyllä",
        buttonTrueColor: "primary",
        buttonFalseText: "Ei",
        buttonFalseColor: "error",
      });
    },
    $hasRole(role) {
      return this.$userInfo?.customAttributes?.roles.includes(role);
    },
    $hasRoleIn(roles) {
      if (!roles) return true;
      return roles.some((r) => {
        if (r === "superAdmin") return this.$isSuperAdmin;
        return this.$userInfo?.customAttributes?.roles.includes(r);
      });
      //return this.$userInfo?.customAttributes?.roles.includes(role);
    },
    $formatRole(role) {
      if (!role) return "";
      const roleNames = {
        admin: "Hälytysosasto: Pääkäyttäjä",
        user: "Hälytysosasto: Käyttäjä",
        hallitus: "Hallitus: Käyttäjä",
        rahastonhoitaja: "Rahastonhoitaja",
        treasurer: "Rahastonhoitaja",
        osastonjohtaja: "Osastonjohtaja",
        naisosasto: "Tukiosasto: Käyttäjä",
        savusukelluskurssi: "Käynyt savusukelluskurssin",
        superAdmin: "Pääkäyttäjä",
      };
      return roleNames[role] || role;
    },
    $createThumbnail(file) {
      return new Promise((resolve, reject) => {
        if (!file || !file.type.startsWith("image/")) {
          reject(new Error("Invalid file type"));
          return;
        }

        const img = new Image();
        const reader = new FileReader();

        // Set a timeout for the operation
        const timeoutId = setTimeout(() => {
          reject(new Error("Thumbnail creation timeout"));
        }, 30000); // 30 seconds timeout

        // When the file is read as a data URL, load it into the image element
        reader.onload = (event) => {
          img.onload = () => {
            try {
              clearTimeout(timeoutId);
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");

              if (!ctx) {
                reject(new Error("Cannot get canvas context"));
                return;
              }

              // Resize the image (you can modify the width and height)
              const width = 150; // Thumbnail width
              const height = (img.height / img.width) * width; // Maintain aspect ratio
              canvas.width = width;
              canvas.height = height;

              // Draw the image onto the canvas
              ctx.drawImage(img, 0, 0, width, height);

              // Convert the canvas to a Blob (thumbnail file)
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    resolve(blob); // Resolve with the thumbnail Blob
                  } else {
                    reject(new Error("Canvas toBlob failed"));
                  }
                },
                "image/jpeg",
                0.8,
              ); // Add quality parameter
            } catch (error) {
              clearTimeout(timeoutId);
              reject(new Error(`Canvas processing error: ${error.message}`));
            }
          };

          img.onerror = () => {
            clearTimeout(timeoutId);
            reject(new Error("Image load failed"));
          };

          img.src = event.target.result; // Set image source to the file data URL
        };

        reader.onerror = (error) => {
          clearTimeout(timeoutId);
          reject(
            new Error(`File read error: ${error.message || "Unknown error"}`),
          );
        };

        // Read the file as a data URL
        try {
          reader.readAsDataURL(file);
        } catch (error) {
          clearTimeout(timeoutId);
          reject(new Error(`FileReader error: ${error.message}`));
        }
      });
    },
    $createPicture(file) {
      return new Promise((resolve, reject) => {
        if (!file || !file.type.startsWith("image/")) {
          reject(new Error("Invalid file type"));
          return;
        }

        const img = new Image();
        const reader = new FileReader();

        // Set a timeout for the operation
        const timeoutId = setTimeout(() => {
          reject(new Error("Picture processing timeout"));
        }, 30000); // 30 seconds timeout

        // When the file is read as a data URL, load it into the image element
        reader.onload = (event) => {
          img.onload = () => {
            try {
              clearTimeout(timeoutId);
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");

              if (!ctx) {
                reject(new Error("Cannot get canvas context"));
                return;
              }

              // Check if the image width is less than 2000 pixels
              if (img.width < 2000) {
                // If the image is smaller than 2000 pixels, use its original dimensions
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0, img.width, img.height);
              } else {
                // Resize the image to a width of 2000 pixels while maintaining aspect ratio
                const width = 2000;
                const height = (img.height / img.width) * width;
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
              }

              // Convert the canvas to a Blob
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    resolve(blob); // Resolve with the processed Blob
                  } else {
                    reject(new Error("Canvas toBlob failed"));
                  }
                },
                "image/jpeg",
                0.9,
              ); // Add quality parameter
            } catch (error) {
              clearTimeout(timeoutId);
              reject(new Error(`Canvas processing error: ${error.message}`));
            }
          };

          img.onerror = () => {
            clearTimeout(timeoutId);
            reject(new Error("Image load failed"));
          };

          img.src = event.target.result; // Set image source to the file data URL
        };

        reader.onerror = (error) => {
          clearTimeout(timeoutId);
          reject(
            new Error(`File read error: ${error.message || "Unknown error"}`),
          );
        };

        // Read the file as a data URL
        try {
          reader.readAsDataURL(file);
        } catch (error) {
          clearTimeout(timeoutId);
          reject(new Error(`FileReader error: ${error.message}`));
        }
      });
    },
  },
  computed: {
    $pwa() {
      return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone ||
        document.referrer.includes("android-app://")
      );
    },
    $iOS() {
      return (
        [
          "iPad Simulator",
          "iPhone Simulator",
          "iPod Simulator",
          "iPad",
          "iPhone",
          "iPod",
        ].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes("Mac") && "ontouchend" in document)
      );
    },
    $user() {
      return this.$store.state.user;
    },
    $userInfo() {
      const userInfo = this.$store.state.user?.reloadUserInfo;
      try {
        userInfo.customAttributes = JSON.parse(userInfo.customAttributes);
      } catch (e) {
        //.
      }
      return userInfo;
    },
    $qualifications() {
      return this.$userInfo.customAttributes?.qualifications || [];
    },
    $userDBContext() {
      return {
        email: this.$userInfo?.email,
        sub: this.$userInfo?.localId,
        user: this.$userInfo?.displayName,
      };
    },
    $isUser() {
      return this.$userInfo?.customAttributes?.roles.includes("user");
    },
    $isAdmin() {
      return this.$userInfo?.customAttributes?.roles.includes("admin");
    },
    $isHallitus() {
      return this.$userInfo?.customAttributes?.roles.includes("hallitus");
    },
    $isNaisosasto() {
      return this.$userInfo?.customAttributes?.roles.includes("naisosasto");
    },
    $isTreasurer() {
      return this.$userInfo?.customAttributes?.roles.includes(
        "rahastonhoitaja",
      );
    },
    $isOnlyNaisosasto() {
      return this.$isNaisosasto && !this.$isAdmin && !this.$isUser;
    },
    $isMissingUserRole() {
      return (
        !this.$userInfo?.customAttributes?.roles ||
        (!this.$userInfo?.customAttributes?.roles?.includes("user") &&
          !this.$userInfo?.customAttributes?.roles?.includes("naisosasto"))
      );
    },
    $isSuperAdmin() {
      let emailList = [
        "jonirautiainen@gmail.com",
        "joni.leino88@gmail.com",
        "jarnojjoensuu@gmail.com",
        "heikkila.kai65@gmail.com",
        "janne.leinonen@outlook.com",
      ];
      return emailList.includes(this.$userInfo?.email);
    },
    $isHPJ() {
      let emailList = ["joni.leino88@gmail.com"];
      return emailList.includes(this.$userInfo?.email);
    },
    $isVihkoAdmin() {
      if (this.$isSuperAdmin) return true;
      let emailList = ["jiiihelin76@gmail.com"];
      return emailList.includes(this.$userInfo?.email);
    },
    $isVihkoSeuranta() {
      let emailList = [
        "jonirautiainen@gmail.com",
        "heikkila.kai65@gmail.com",
        "jiiihelin76@gmail.com",
      ];
      return emailList.includes(this.$userInfo?.email);
    },
    $isTyosuojelutoimikunta() {
      let emailList = [
        "jarnojjoensuu@gmail.com",
        "heikkila.kai65@gmail.com",
        "jiiihelin76@gmail.com",
      ];
      return emailList.includes(this.$userInfo?.email);
    },
    $isKalustoTarveilmoittaja() {
      if (this.$isSuperAdmin) return true;
      let emailList = [
        "heikkila.kai65@gmail.com",
        "nvehkalahti@gmail.com",
        "jonirautiainen@gmail.com",
        "joni.leino88@gmail.com",
        "jarnojjoensuu@gmail.com",
        "jla.ylinen@gmail.com",
        "pkanersalo@gmail.com",
      ];
      return emailList.includes(this.$userInfo?.email);
    },
    $isTarveilmoitusVastuuhenkilo() {
      //if (this.$isSuperAdmin) return true;
      let emailList = [
        "jarnojjoensuu@gmail.com",
        "jla.ylinen@gmail.com",
        "heikkila.kai65@gmail.com",
        "joni.leino88@gmail.com",
        "pkanersalo@gmail.com",
      ];
      return emailList.includes(this.$userInfo?.email);
    },
    $isTukiosastonHarjoituksetAdmin() {
      if (this.$isSuperAdmin) return true;
      let emailList = ["laura.laitila@icloud.com"];
      return emailList.includes(this.$userInfo?.email);
    },
    $rules() {
      return {
        required: (value) => !!value || "Tämä kenttä ei voi olla tyhjä.",
        counter: (value) => value.length <= 20 || "Max 20 characters",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return (
            pattern.test(value) || "Oikean muotoinen sähköposti vaaditaan."
          );
        },
      };
    },
  },
});
